import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled, { css } from "styled-components/macro";
import { lighten, darken, rem } from "polished";
import ReactGA from "react-ga";
import { ApplicationType } from "csv-package";

import { ReactComponent as DeleteIcon } from "../assets/icons/Delete.svg";
import { ReactComponent as DownArrowOutlined } from "../assets/icons/DownArrowOutlined.svg";

import { hasAnonymousIdentity, removeAnonymousIdentity } from "../helpers/auth";
import api from "../helpers/api";

import MenuBar from "../components/MenuBar";
import Button from "../components/Button";
import LoadingIndicator from "../components/LoadingIndicator";
import ConfirmationPopup from "../components/ConfirmationPopup";
import getCourtById from "../helpers/getCourtById";
import colors from "../theme/colors";
import media from "../theme/media";
import {
  config,
  configJurisdiction,
  configApplicationType,
} from "../config/systemConfig";
import InactivityCheck from "../components/InactivityCheck";
import { Formik } from "formik";

/**
 * Application status constants used for filtering and displaying application states
 */
const APPLICATION_STATUSES = {
  SUBMITTED: [
    "dnp",
    "archived",
    "submitted",
    "verified",
    "re-declare-accepted",
  ],
  RE_DECLARE_REQUESTED: "re-declare-requested",
  RE_DECLARE_ACCEPTED: "re-declare-accepted",
  INITIATED: "initiated",
} as const;

/**
 * Application type mapping for display purposes
 */
const APPLICATION_TYPE_LABELS = {
  N: "New",
  E: "Extend", 
  V: "Vary",
  R: "Revoke",
  EV: "Extend and Vary",
  EVR: "Extend, Vary or Revoke",
} as const;

/**
 * Helper function to get the display label for an application type
 */
const getApplicationTypeLabel = (type: string): string => {
  return APPLICATION_TYPE_LABELS[type as keyof typeof APPLICATION_TYPE_LABELS] || type;
};

const FVIO_LOGIN_URL = "https://fvio.mcv.vic.gov.au/login";
const EVR_LOGIN_URL = "https://evr.mcv.vic.gov.au/login";

const Applications: React.FC = () => {
  const { push, replace } = useHistory();
  const { pathname } = useLocation();
  // Applications in the primary portal application type (e.g. FVIO)
  const [applications, setApplications] = React.useState<ApplicationType[]>([]);
  // Applications in other application types (ie. not the primary portal application type) (e.g. EVR if the primary is FVIO)
  const [nonCurrentTypeApplications, setNonCurrentTypeApplications] =
    React.useState<ApplicationType[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showPortalConfirmation, setShowPortalConfirmation] =
    React.useState(false);
  const [isOtherPortalsAccordionOpen, setIsOtherPortalsAccordionOpen] =
    React.useState(false);

  // on mount fetch applciations
  React.useEffect(() => {
    ReactGA.pageview(pathname);

    setLoading(true);
    const fetchApplications = async () => {
      let result;
      try {
        // get applications by identity
        result = (await api("get", "/public/application")) as ApplicationType[];

        if (!hasAnonymousIdentity()) {
          setLoading(false);

          /* Match function for current jurisdiction and applicatiosn valid for the current portal application type
           * The Jurisdiction is always FVIO, the application types accepted are:
           * "FVIO" portal (for new applications only) "N"
           * "EVR" portal (for extend, vary or revoke applications) "E", "V", "R", "EV", "EVR"
          */
          const isCurrentTypeApplication = (application: ApplicationType) =>
            application.Jurisdiction === configJurisdiction &&
            config.validApplicationTypes.includes(application.Type);

          // Filter applications based on the match function (current jurisdiction and application type)
          const filteredCurrentTypeApplications = result.filter(
            isCurrentTypeApplication
          );

          // Filter applications that are not valid for the current portal application type (other jurisdictions or other application types)
          const filteredNonCurrentTypeApplications = result.filter(
            (app) => !isCurrentTypeApplication(app)
          );

          setApplications(filteredCurrentTypeApplications);
          setNonCurrentTypeApplications(filteredNonCurrentTypeApplications);

          return;
        }

        if (result.length === 0) {
          // In this case, there's still an identity in the local storage, but there are no applications in progress. Create a new identity then a new application
          setLoading(false);
          removeAnonymousIdentity();
          replace("/application/create/anonymous");
          return;
        }

        // For redeclarations, an identity is created every time it's being accessed using the redeclare key
        replace(`/application/${result[0].ApplicationID}`);
      } catch (err: any) {
        alert(err.message);
      }
    };

    fetchApplications();
  }, [push, pathname, replace]);

  const deleteApplication = React.useCallback(
    async (ApplicationID) => {
      if (
        !window.confirm("Are you sure you want to delete this application?")
      ) {
        return null;
      }

      // stored application for optomisitic update.
      const storedObject = applications.filter(
        (application) => application.ApplicationID === ApplicationID
      ) as ApplicationType[];

      try {
        // filter out application
        setApplications(
          applications.filter(
            (application) => application.ApplicationID !== ApplicationID
          )
        );

        await api("del", `/public/application/${ApplicationID}`);
      } catch (error: any) {
        setApplications([...applications, ...storedObject]);
        alert(error.message);
      }
    },
    [applications]
  );

  /**
   * Handles the portal redirect confirmation by opening the appropriate login URL
   * based on the current application type and closing the confirmation popup
   */
  const handlePortalRedirectConfirmation = React.useCallback(() => {
    const loginUrl =
      configApplicationType === "EVR" ? FVIO_LOGIN_URL : EVR_LOGIN_URL;
    window.open(loginUrl, "_blank", "noopener,noreferrer");
    setShowPortalConfirmation(false);
  }, []);

  /**
   * Handles closing the portal redirect confirmation popup
   */
  const handlePortalRedirectConfirmationClose = React.useCallback(() => {
    setShowPortalConfirmation(false);
  }, []);

  if (hasAnonymousIdentity()) {
    return (
      <LoadingWrap aria-live="polite" aria-label="Loading applications">
        <LoadingIndicator />
      </LoadingWrap>
    );
  }

  return (
    <Wrapper>
      <MenuBar crumbs={[{ text: "Applications" }]} hideSaveIndicator />
      <Inner>
        <h2>
          {
            configApplicationType === "EVR"
              ? "My extend/vary or revoke applications"
              : "My family violence intervention order applications"
          }
        </h2>

        {configApplicationType === "EVR" ? (
          <>
            <p>
              This view shows any extend, vary or revoke applications you have
              created using this login.
            </p>
            <p>
              To view new family violence intervention order (FVIO) applications{" "}
              <a href={FVIO_LOGIN_URL}>
                login here
              </a>
              .
            </p>
          </>
        ) : (
          <>
            <p>
              This view shows any new family violence intervention order applications
              you have created using this login.
            </p>
            <p>
              To view any extend, vary or revoke (EVR) applications{" "}
              <a href={EVR_LOGIN_URL}>
                login here
              </a>
              .
            </p>
          </>
        )}

        <Buttons>
          <Button action={() => push(`/application/create`)}>
            Start new application
          </Button>
        </Buttons>
        <Formik initialValues={{}} onSubmit={(values, actions) => {}}>
          <InactivityCheck hasForm={false} />
        </Formik>
        {loading ? (
          <div aria-live="polite" aria-label="Loading applications">
            <LoadingIndicator />
          </div>
        ) : (
          <>
            {/* Applications in the primary portal application type (e.g. FVIO) */}
            <ul>
              <>
                {applications.length === 0 && (
                  <li>
                    <h2>You have no applications</h2>
                  </li>
                )}
                {applications
                  .sort((a, b) => (a.Created > b.Created ? -1 : 1))
                  .map((application) => {
                    const court = getCourtById(application.CourtID);

                    return (
                      <li key={application.ApplicationID}>
                        <div>
                          <Number>
                            {application.ApplicationID} ({application.Type})
                          </Number>
                          <p>
                            <b>Started:</b>{" "}
                            {new Date(application.Created).toDateString()}
                            {court && (
                              <>
                                <br />
                                <b>Court:</b> {court.name}
                              </>
                            )}
                            {application.Type && (
                              <>
                                <br />
                                <b>Application Type:</b> {application.Type} ({getApplicationTypeLabel(application.Type)})
                              </>
                            )}
                          </p>
                        </div>
                        <Right>
                          {application.Status &&
                          APPLICATION_STATUSES.SUBMITTED.includes(
                            application.Status as any
                          ) ? (
                            <>
                              <SubmittedTag />
                              <br />
                              <EditButton
                                to={`/application/${application.ApplicationID}`}
                              >
                                {application.Status ===
                                APPLICATION_STATUSES.RE_DECLARE_ACCEPTED
                                  ? "View re-redeclared application"
                                  : "View"}
                              </EditButton>
                            </>
                          ) : (
                            <>
                              <EditButton
                                to={`/application/${application.ApplicationID}`}
                              >
                                {application.Status ===
                                APPLICATION_STATUSES.RE_DECLARE_REQUESTED
                                  ? "RE-DECLARE"
                                  : "EDIT"}
                              </EditButton>

                              {application.Status ===
                                APPLICATION_STATUSES.INITIATED && (
                                <DeleteButton
                                  as="button"
                                  onClick={() =>
                                    deleteApplication(application.ApplicationID)
                                  }
                                  aria-label={`Delete application ${application.ApplicationID}`}
                                >
                                  <DeleteIcon /> Delete
                                </DeleteButton>
                              )}
                            </>
                          )}
                        </Right>
                      </li>
                    );
                  })}
              </>
            </ul>

            {/* Applications of other types (ie. not the primary portal application type) (e.g. EVR if the primary is FVIO) */}
            <OtherPortalsSection>
              {nonCurrentTypeApplications.length > 0 && (
                <>
                  <OtherPortalsHeader
                    onClick={() =>
                      setIsOtherPortalsAccordionOpen(
                        !isOtherPortalsAccordionOpen
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsOtherPortalsAccordionOpen(
                          !isOtherPortalsAccordionOpen
                        );
                      }
                    }}
                    aria-expanded={isOtherPortalsAccordionOpen}
                    aria-controls="other-portals-content"
                    tabIndex={0}
                    role="button"
                  >
                    <h4>Application(s) in {configApplicationType === "EVR" ? "FVIO" : "EVR"} portal</h4>
                    <DownIcon
                      isopen={isOtherPortalsAccordionOpen ? "yes" : undefined}
                    />
                  </OtherPortalsHeader>
                  <OtherPortalsContent
                    id="other-portals-content"
                    isOpen={isOtherPortalsAccordionOpen ? "yes" : undefined}
                  >
                    <OtherPortalsContentWrapper>
                      {configApplicationType === "EVR" ? (
                        <>
                          <p>
                            You are currently logged in to the Extend Vary Revoke (EVR) portal.
                          </p>

                          <p>
                            Any new applications for a Family Violence Intervention Order (FVIO) can be seen listed below if they were created within the FVIO portal using this login. 
                          </p>

                          <p>
                            If you are the Affected Family Member who created a FVIO application through the FVIO portal you can view your application/s, by selecting the button below. This will redirect you to the FVIO login page.
                          </p>

                          <p>
                            Please use your same login details as used for your online EVR application.
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            You are currently logged in to the Family Violence Intervention Order (FVIO) portal.
                          </p>

                          <p>
                            Any new applications for a Extend Vary Revoke (EVR) can be seen listed below if they were created within the EVR portal using this login. 
                          </p>

                          <p>
                            If you are the Affected Family Member who created a EVR application through the EVR portal you can view your application/s, by selecting the button below. This will redirect you to the EVR login page.
                          </p>

                          <p>
                            Please use your same login details as used for your online FVIO application.
                          </p>
                        </>
                      )}

                      <Buttons>
                        <Button action={() => setShowPortalConfirmation(true)}>
                          Login to{" "}
                          {configApplicationType === "EVR" ? "FVIO" : "EVR"}
                          {" "}portal
                        </Button>
                      </Buttons>
                      <ul>
                        {nonCurrentTypeApplications
                          .sort((a, b) => (a.Created > b.Created ? -1 : 1))
                          .map((application) => {
                            const court = getCourtById(application.CourtID);

                            return (
                              <li key={application.ApplicationID}>
                                <div>
                                  <Number>
                                    {application.ApplicationID} (
                                    {application.Type})
                                  </Number>
                                  <p>
                                    <b>Started:</b>{" "}
                                    {new Date(
                                      application.Created
                                    ).toDateString()}

                                    <br />
                                    <b>Court:</b> {court?.name ? court?.name : 'No court set'}

                                    <br />
                                    <b>Application Type:</b> {application.Type} ({getApplicationTypeLabel(application.Type)})
                                  </p>
                                </div>
                                <Right>
                                  {application.Status &&
                                  APPLICATION_STATUSES.SUBMITTED.includes(
                                    application.Status as any
                                  ) ? (
                                    <SubmittedTag />
                                  ) : (
                                    <InProgressTag />
                                  )}
                                </Right>
                              </li>
                            );
                          })}
                      </ul>
                    </OtherPortalsContentWrapper>
                  </OtherPortalsContent>
                </>
              )}
            </OtherPortalsSection>
          </>
        )}
      </Inner>
      <ConfirmationPopup
        visible={showPortalConfirmation}
        onCloseClick={handlePortalRedirectConfirmationClose}
        onOkClick={handlePortalRedirectConfirmation}
        displayText={`You are about to move to the ${
          configApplicationType === "EVR" ? "FVIO" : "EVR"
        } portal. You will be need to login again to verify your identity.`}
      /> 
     
    </Wrapper>
  );
};

export default Applications;

const Wrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 600px;
  min-height: calc(100vh - 200px);
  padding: 100px 15px 80px 15px;
  ul {
    padding: 0;
    border-top: 1px solid black;
  }
  li {
    padding: 20px 0;
    list-style-type: none;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    border-bottom: 1px solid black;
    ${media.lessThan("tablet")`
      flex-direction: column;
    `}
  }
`;

const Number = styled.p`
  font-size: ${rem(18)};
  font-weight: 700;
  margin-top: 0;
`;

const EditButton = styled(Link)`
  text-transform: uppercase;
  display: inline-block;
  height: 1.6em;
  line-height: 1.4em;
  border-radius: 0.8em;
  background: ${colors.blue};
  text-align: center;
  min-width: 5em;
  text-decoration: none;
  color: white;
  padding: 0.2em 0.6em 0;

  &:hover {
    background: ${darken(0.2, colors.blue)};
  }

  &:active {
    background: ${lighten(0.2, colors.blue)};
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  color: #000000;
  padding: 0;
  margin: 0;
  border: 0;
  display: flex;
  align-items: center;
  color: #4a4a4a;
  cursor: pointer;
  > svg {
    height: 1.5em;
    width: 1.5em;
    transform: translateY(-0.1em);
  }

  &:hover {
    color: ${lighten(0.2, `#4a4a4a`)};
  }

  &:focus {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;

  ${media.lessThan("tablet")`
    align-items: flex-start;
    a, button {
      margin-bottom: 0.5em;
    }
  `}
`;

const SubmittedTag = styled.div`
  background: #d5d5d5;
  color: black;
  display: flex;
  align-items: center;
  height: 1.5em;
  border-radius: 0.8em;
  padding: 0.1em 1.1em 0;
  &:after {
    content: "SUBMITTED";
  }
  &::before {
    content: "Status: ";
    position: absolute;
    left: -9999px;
  }
`;

const InProgressTag = styled.div`
  background: #d5d5d5;
  color: black;
  display: flex;
  align-items: center;
  height: 1.5em;
  border-radius: 0.8em;
  padding: 0.1em 1.1em 0;
  &:after {
    content: "IN PROGRESS";
  }
  &::before {
    content: "Status: ";
    position: absolute;
    left: -9999px;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  button + button {
    margin-top: ${rem(8)};
  }

  ${media.lessThan("tablet")`
    align-items: flex-start;
    margin-bottom: 1.5em;
  `}
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 246px);
`;

const OtherPortalsSection = styled.div`
  margin-top: 40px;
  color: #666666;
`;

const OtherPortalsHeader = styled.button`
  width: 100%;
  min-height: 48px;
  padding: 5px 15px 5px;
  background: rgba(195, 214, 234, 0.2);
  margin: 0;
  font-weight: 700;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border: none;
  border-radius: 4px;
  text-align: left;
  cursor: pointer;

  h4 {
    font-size: ${rem(18)};
    color: #444444;
    margin: 0;
  }

  &:hover {
    background: rgba(195, 214, 234, 0.3);
  }

  &:focus {
    outline: 2px solid ${colors.blue};
    outline-offset: 2px;
  }
`;

const OtherPortalsContent = styled.div<{ isOpen?: string }>`
  max-height: 0;
  overflow: hidden;
  overflow-y: auto;
  transition: max-height 0.2s linear;
  background-color: rgb(248, 252, 255);

  &:before {
    content: "";
    display: block;
    height: 7px;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      max-height: 2000px;
    `}
`;

const OtherPortalsContentWrapper = styled.div`
  padding: 20px;
`;

const DownIcon = styled(DownArrowOutlined)<{ isopen?: string }>`
  width: 11px;
  height: 8px;
  transition: all 0.2s linear;
  ${({ isopen }) =>
    isopen === "yes" &&
    css`
      transform: rotate(180deg);
    `}
`;
